import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase/firebaseConfig';
import { ref, onValue, update } from 'firebase/database';
import { useAuth } from '../../context/AuthContext';
import './AdminSitemap.css';

function AdminSitemap() {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'superadmin' || userProfile?.role === 'moderator' || userProfile?.role === 'support';
  
  const [visibilityMap, setVisibilityMap] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const sitemapData = [
    {
      category: "Public Channels",
      icon: "fa-solid fa-earth-americas",
      routes: [
        { id: "home", path: "/", name: "Landing Page", description: "Primary platform entry point." },
        { id: "about", path: "/about", name: "About Ecosystem", description: "Vision and mission documentation." },
        { id: "docs", path: "/docs", name: "Developer Docs", description: "Technical integration guides." },
        { id: "changelog", path: "/changelog", name: "Patch Notes", description: "Version history and evolution tracking." },
        { id: "faq", path: "/faq", name: "FAQ Portal", description: "Security and account procedures." },
        { id: "login", path: "/login", name: "Authentication", description: "Secure entry gate." },
        { id: "register", path: "/register", name: "Citizen Enrollment", description: "New developer registration." },
        { id: "sitemap", path: "/sitemap", name: "Platform Sitemap", description: "This navigational index." }
      ]
    },
    {
      category: "Citizen Workspace",
      icon: "fa-solid fa-user-gear",
      routes: [
        { id: "dashboard", path: "/dashboard", name: "Master Dashboard", description: "Core user control center." },
        { id: "public_profile", path: "/dev/:id", name: "Public Profile", description: "Live developer persona view." },
        { id: "suspended", path: "/suspended", name: "Restricted Access", description: "Security violation landing page." },
        { id: "report", path: "/report", name: "Security Report", description: "User-initiated incident logging." }
      ]
    },
    {
      category: "Origin Core (Admin)",
      icon: "fa-solid fa-microchip",
      routes: [
        { id: "admin_dash", path: "/admin/dashboard", name: "Admin Nexus", description: "High-level platform oversight." },
        { id: "admin_users", path: "/admin/users", name: "Citizen Directory", description: "Total user moderation vault." },
        { id: "admin_reports", path: "/admin/reports", name: "Audit Queue", description: "ID changes and security reports." },
        { id: "admin_ai", path: "/admin/ai-intel", name: "AI Hub", description: "Platform-wide intelligence analytics." },
        { id: "admin_settings", path: "/admin/settings", name: "Core Protocol", description: "System-wide maintenance & toggles." }
      ]
    }
  ];

  useEffect(() => {
    const visibilityRef = ref(db, 'settings/sitemap_visibility');
    const unsubscribe = onValue(visibilityRef, (snapshot) => {
      if (snapshot.exists()) {
        setVisibilityMap(snapshot.val());
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const toggleVisibility = async (routeId) => {
    if (!isAdmin) return;
    try {
      const currentVal = visibilityMap[routeId] !== false; // Default to true if undefined
      await update(ref(db, 'settings/sitemap_visibility'), {
        [routeId]: !currentVal
      });
    } catch (err) {
      console.error("Failed to update visibility", err);
    }
  };

  // Filter logic for non-admins
  const filteredData = isAdmin ? sitemapData : sitemapData.map(section => ({
    ...section,
    routes: section.routes.filter(route => visibilityMap[route.id] !== false)
  })).filter(section => section.routes.length > 0);

  if (loading) return <div className="page-loader">Syncing System Architecture...</div>;

  return (
    <div className="admin-sitemap page-container animate-up">
      <div className="container">
        <header className="sitemap-header">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px'}}>
            <div>
              <span className="sitemap-tag">{isAdmin ? "SYSTEM ARCHITECTURE" : "NAVIGATION INDEX"}</span>
              <h1>Platform Sitemap</h1>
              <p className="subtitle">
                {isAdmin 
                  ? "Centralized inventory of all accessible routes and security protocols." 
                  : "Explore the DevTracker platform architecture and accessible modules."}
              </p>
            </div>
            {isAdmin && (
              <button 
                className={`btn-resolve ${editMode ? 'active' : ''}`} 
                onClick={() => setEditMode(!editMode)}
                style={{margin: 0, background: editMode ? '#00ffae' : 'transparent', color: editMode ? '#0d1117' : '#00ffae', border: '1px solid #00ffae'}}
              >
                <i className={`fa-solid ${editMode ? 'fa-check-circle' : 'fa-gear'}`}></i>
                {editMode ? "Save Visibility" : "Manage Visibility"}
              </button>
            )}
          </div>
        </header>

        <div className="sitemap-grid">
          {filteredData.map((section, idx) => (
            <div key={idx} className="sitemap-card glass-card">
              <div className="sitemap-card-header">
                <i className={section.icon}></i>
                <h3>{section.category}</h3>
              </div>
              <div className="sitemap-links">
                {section.routes.map((route, rIdx) => {
                  const isVisible = visibilityMap[route.id] !== false;
                  return (
                    <div key={rIdx} className={`sitemap-link-item ${(!isVisible && editMode) ? 'hidden-item' : ''}`}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Link to={route.path} className="sitemap-link-main">
                          {route.name} <span className="sitemap-path">{route.path}</span>
                        </Link>
                        {isAdmin && editMode && (
                          <div 
                            className={`visibility-toggle ${isVisible ? 'on' : 'off'}`}
                            onClick={() => toggleVisibility(route.id)}
                            title={isVisible ? "Visible to Public" : "Hidden from Public"}
                          >
                            <i className={`fa-solid ${isVisible ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                          </div>
                        )}
                      </div>
                      <p className="sitemap-desc">{route.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <footer className="sitemap-footer">
          <div className="security-layer-badge">
            <i className="fa-solid fa-shield-virus"></i>
            <span>SECURITY PROTOCOL: <strong>SEC-v2.5.2-ORIGIN</strong></span>
          </div>
          <p>{isAdmin ? "Restricted access. All navigation cycles are logged by Origin Core." : "Verified platform routes for authorized Citizen access."}</p>
        </footer>
      </div>
    </div>
  );
}

export default AdminSitemap;
